import type {
  TrafficAttributionFields,
  WindowWorkyMarketingDataLayer,
  WorkyMarketingAnalyticsEventBody,
} from './WorkyMarketingAnalyticsInterfaces';
import { TAG_DICTIONARY } from './TagDictionary';
import {
  getSessionStorageValue,
  isValidString,
  LOCAL_STORAGE_KEYS,
} from '../common/CommonUtilsMethods';

const REFERRER_MAX_LENGTH: number = 500;

const getFirstTruthyFromParams = (
  searchParams: URLSearchParams,
  keys: readonly string[],
): string | undefined => {
  for (const key of keys) {
    const candidate: string | null = searchParams.get(key);
    if (candidate !== null && candidate.trim().length > 0) {
      return candidate;
    }
  }
  return undefined;
};

const normalizeReferrerMarketing = (
  referrer: string | undefined,
): string | undefined => {
  if (referrer === undefined || referrer.trim().length === 0) {
    return undefined;
  }
  return referrer.slice(0, REFERRER_MAX_LENGTH);
};

const extractTrafficFromLocationMarketing = (
  locationHref: string,
  referrer: string | undefined,
): TrafficAttributionFields => {
  try {
    const urlObj: URL = new URL(locationHref);
    const searchParams: URLSearchParams = urlObj.searchParams;
    const result: TrafficAttributionFields = {
      utm_source: getFirstTruthyFromParams(searchParams, [
        TAG_DICTIONARY.PARAMS.UTM_SOURCE,
      ]),
      utm_medium: getFirstTruthyFromParams(searchParams, [
        TAG_DICTIONARY.PARAMS.UTM_MEDIUM,
      ]),
      utm_campaign: getFirstTruthyFromParams(searchParams, [
        TAG_DICTIONARY.PARAMS.UTM_CAMPAIGN,
      ]),
      utm_content: getFirstTruthyFromParams(searchParams, [
        TAG_DICTIONARY.PARAMS.UTM_CONTENT,
      ]),
      utm_term: getFirstTruthyFromParams(searchParams, [
        TAG_DICTIONARY.PARAMS.UTM_TERM,
      ]),
      gclid: getFirstTruthyFromParams(searchParams, [TAG_DICTIONARY.PARAMS.GCLID]),
      fbclid: getFirstTruthyFromParams(searchParams, [TAG_DICTIONARY.PARAMS.FBCLID]),
      msclkid: getFirstTruthyFromParams(searchParams, [
        TAG_DICTIONARY.PARAMS.MSCLKID,
      ]),
    };
    const normalizedReferrer: string | undefined = normalizeReferrerMarketing(
      referrer,
    );
    if (normalizedReferrer !== undefined) {
      return { ...result, referrer: normalizedReferrer };
    }
    return result;
  } catch {
    return {};
  }
};

const dropEmptyAnalyticsValues = (
  input: Record<string, string>,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value.length > 0) {
      out[key] = value;
    }
  }
  return out;
};
const trafficToFlatParamMap = (
  traffic: TrafficAttributionFields,
): Record<string, string> => {
  const flat: Record<string, string> = {};
  if (
    typeof traffic.utm_source === 'string' &&
    traffic.utm_source.length > 0
  ) {
    flat[TAG_DICTIONARY.PARAMS.UTM_SOURCE] = traffic.utm_source;
  }
  if (
    typeof traffic.utm_medium === 'string' &&
    traffic.utm_medium.length > 0
  ) {
    flat[TAG_DICTIONARY.PARAMS.UTM_MEDIUM] = traffic.utm_medium;
  }
  if (
    typeof traffic.utm_campaign === 'string' &&
    traffic.utm_campaign.length > 0
  ) {
    flat[TAG_DICTIONARY.PARAMS.UTM_CAMPAIGN] = traffic.utm_campaign;
  }
  if (
    typeof traffic.utm_content === 'string' &&
    traffic.utm_content.length > 0
  ) {
    flat[TAG_DICTIONARY.PARAMS.UTM_CONTENT] = traffic.utm_content;
  }
  if (typeof traffic.utm_term === 'string' && traffic.utm_term.length > 0) {
    flat[TAG_DICTIONARY.PARAMS.UTM_TERM] = traffic.utm_term;
  }
  if (typeof traffic.gclid === 'string' && traffic.gclid.length > 0) {
    flat[TAG_DICTIONARY.PARAMS.GCLID] = traffic.gclid;
  }
  if (typeof traffic.fbclid === 'string' && traffic.fbclid.length > 0) {
    flat[TAG_DICTIONARY.PARAMS.FBCLID] = traffic.fbclid;
  }
  if (typeof traffic.msclkid === 'string' && traffic.msclkid.length > 0) {
    flat[TAG_DICTIONARY.PARAMS.MSCLKID] = traffic.msclkid;
  }
  if (typeof traffic.referrer === 'string' && traffic.referrer.length > 0) {
    flat[TAG_DICTIONARY.PARAMS.REFERRER] = traffic.referrer;
  }
  return flat;
};

const flattenEventBodyMarketing = (
  body: WorkyMarketingAnalyticsEventBody,
): Record<string, string> => {
  switch (body.event) {
    case TAG_DICTIONARY.EVENTS.PAGE_VIEW:
      return {
        event: body.event,
        [TAG_DICTIONARY.PARAMS.PAGE_PATH]: body.page_path,
        [TAG_DICTIONARY.PARAMS.PAGE_LOCATION]: body.page_location,
        [TAG_DICTIONARY.PARAMS.PAGE_TITLE]: body.page_title,
      };
    case TAG_DICTIONARY.EVENTS.CTA_CLICK:
      return {
        event: body.event,
        [TAG_DICTIONARY.PARAMS.CTA_ID]: body.cta_id,
        ...(body.cta_label !== undefined
          ? { [TAG_DICTIONARY.PARAMS.CTA_LABEL]: body.cta_label }
          : {}),
      };
    case TAG_DICTIONARY.EVENTS.FORM_START:
      return {
        event: body.event,
        [TAG_DICTIONARY.PARAMS.FORM_ID]: body.form_id,
        ...(body.form_name !== undefined
          ? { [TAG_DICTIONARY.PARAMS.FORM_NAME]: body.form_name }
          : {}),
      };
    case TAG_DICTIONARY.EVENTS.FORM_SUBMIT:
      return {
        event: body.event,
        [TAG_DICTIONARY.PARAMS.FORM_ID]: body.form_id,
        ...(body.form_name !== undefined
          ? { [TAG_DICTIONARY.PARAMS.FORM_NAME]: body.form_name }
          : {}),
      };
    case TAG_DICTIONARY.EVENTS.DEMO_BOOKED:
      return {
        event: body.event,
        [TAG_DICTIONARY.PARAMS.CALENDLY_BOOKING_SOURCE]:
          body.calendly_booking_source,
        ...(body.calendly_event_type_name !== undefined
          ? {
              [TAG_DICTIONARY.PARAMS.CALENDLY_EVENT_TYPE_NAME]:
                body.calendly_event_type_name,
            }
          : {}),
        ...(body.calendly_scheduled_event_uri_suffix !== undefined
          ? {
              [TAG_DICTIONARY.PARAMS.CALENDLY_SCHEDULED_EVENT_URI_SUFFIX]:
                body.calendly_scheduled_event_uri_suffix,
            }
          : {}),
      };
  }
};

const pushObjectToMarketingDataLayer = (
  merged: Record<string, string>,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const win: Window & WindowWorkyMarketingDataLayer = window as Window &
      WindowWorkyMarketingDataLayer;
    const layer: Array<Record<string, unknown>> = Array.isArray(win.dataLayer)
      ? win.dataLayer
      : [];
    win.dataLayer = layer;
    layer.push({
      ...(merged as unknown as Record<string, unknown>),
    });
  } catch {
    /* Never break the app for measurement; GTM may replace or freeze dataLayer. */
  }
};

const mergeWorkyUserIdIntoMarketingPayload = (
  merged: Record<string, string>,
): Record<string, string> => {
  const workyUserIdRaw: string | null = getSessionStorageValue({
    key: LOCAL_STORAGE_KEYS.USER_ID,
  });
  if (!isValidString(workyUserIdRaw)) {
    return merged;
  }
  return {
    ...merged,
    [TAG_DICTIONARY.PARAMS.USER_ID]: workyUserIdRaw,
  };
};

const mergeTrafficAndMarketingBodyMarketing = (
  locationHref: string,
  referrer: string | undefined,
  body: WorkyMarketingAnalyticsEventBody,
): Record<string, string> => {
  const traffic: TrafficAttributionFields =
    extractTrafficFromLocationMarketing(locationHref, referrer);
  const flatTraffic: Record<string, string> = trafficToFlatParamMap(traffic);
  const eventFlat: Record<string, string> = flattenEventBodyMarketing(body);
  const mergedBase: Record<string, string> = dropEmptyAnalyticsValues({
    ...flatTraffic,
    ...eventFlat,
  });
  return mergeWorkyUserIdIntoMarketingPayload(mergedBase);
};

const pushWorkyMarketingAnalyticsEvent = (
  body: WorkyMarketingAnalyticsEventBody,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const merged = mergeTrafficAndMarketingBodyMarketing(
      window.location.href,
      typeof document !== 'undefined'
        ? document.referrer ?? undefined
        : undefined,
      body,
    );
    pushObjectToMarketingDataLayer(merged);
  } catch {
    /* Malformed URLs, future TAG_DICTIONARY / payload bugs — never surface to UI. */
  }
};

const CALENDLY_BOOKING_RETURN_HINT_QUERY_KEYS: readonly string[] = [
  'assigned_to',
  'event_type_uuid',
  'event_type_name',
  'event_start_time',
  'event_end_time',
  'invitee_uuid',
  'invitee_full_name',
  'invitee_email',
  'answer_1',
];

const isLikelyCalendlyBookingReturnHrefMarketing = (
  hrefRaw: string,
): boolean => {
  try {
    const parsedUrl: URL = new URL(hrefRaw);
    for (const hintKey of CALENDLY_BOOKING_RETURN_HINT_QUERY_KEYS) {
      const queryValue: string | null = parsedUrl.searchParams.get(hintKey);
      if (queryValue !== null && queryValue.trim().length > 0) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

const readRawQueryFromHrefMarketing = (
  hrefRaw: string,
  key: string,
): string | undefined => {
  try {
    const parsedUrl: URL = new URL(hrefRaw);
    const rawValue: string | null = parsedUrl.searchParams.get(key);
    if (rawValue === null || rawValue.trim().length === 0) {
      return undefined;
    }
    return rawValue;
  } catch {
    return undefined;
  }
};

const readDecodedQueryFromHrefMarketing = (
  hrefRaw: string,
  key: string,
): string | undefined => {
  const rawValue: string | undefined = readRawQueryFromHrefMarketing(
    hrefRaw,
    key,
  );
  if (rawValue === undefined) {
    return undefined;
  }
  try {
    return decodeURIComponent(rawValue.replace(/\+/g, '%20'));
  } catch {
    return rawValue;
  }
};

const persistCalendlyReturnDemoDedupOnceMarketingKey = (
  dedupeFingerprint: string,
  channel: string,
): boolean => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false;
  }
  const storageFullKey = `${TAG_DICTIONARY.SESSION_KEYS.CALENDLY_DEMO_BOOKED_DEDUP_PREFIX}_${channel}_${dedupeFingerprint}`;
  try {
    if (sessionStorage.getItem(storageFullKey) !== null) {
      return false;
    }
    sessionStorage.setItem(storageFullKey, '1');
    return true;
  } catch {
    /* QuotaExceededError / SecurityError / private mode — cannot persist dedupe; still emit conversion (possible duplicate vs crashing or dropping). */
    return true;
  }
};

const pushWorkyMarketingDemoBookedFromCalendlyReturnHrefOnce = (
  hrefRaw: string,
): void => {
  if (
    typeof window === 'undefined' ||
    typeof sessionStorage === 'undefined'
  ) {
    return;
  }
  if (!isLikelyCalendlyBookingReturnHrefMarketing(hrefRaw)) {
    return;
  }
  const inviteeRaw: string | undefined = readRawQueryFromHrefMarketing(
    hrefRaw,
    'invitee_uuid',
  );
  const dedupeFingerprint: string =
    typeof inviteeRaw === 'string' && inviteeRaw.length > 0
      ? inviteeRaw
      : `fallback_${String(hrefRaw.length)}_${hrefRaw.slice(0, 80)}`;

  const isFirstDedupMarketing: boolean = persistCalendlyReturnDemoDedupOnceMarketingKey(
    dedupeFingerprint,
    'redirect',
  );

  if (!isFirstDedupMarketing) {
    return;
  }

  const decodedEventTypeName: string | undefined =
    readDecodedQueryFromHrefMarketing(hrefRaw, 'event_type_name');

  pushWorkyMarketingAnalyticsEvent({
    event: TAG_DICTIONARY.EVENTS.DEMO_BOOKED,
    calendly_booking_source:
      TAG_DICTIONARY.CALENDLY_BOOKING_SOURCES.REDIRECT_RETURN,
    ...(decodedEventTypeName !== undefined &&
    decodedEventTypeName.length > 0
      ? { calendly_event_type_name: decodedEventTypeName }
      : {}),
  });
};

const pushWorkyMarketingDemoBookedFromCalendlyEmbedScheduledOnce = (
  scheduledEventUri: string,
): void => {
  if (
    typeof window === 'undefined' ||
    typeof sessionStorage === 'undefined'
  ) {
    return;
  }
  const uriSegmentsFiltered: readonly string[] = scheduledEventUri
    .split('/')
    .filter((segmentEntry: string) => segmentEntry.length > 0);
  const suffixSegment: string =
    uriSegmentsFiltered.length > 0
      ? uriSegmentsFiltered[uriSegmentsFiltered.length - 1]
      : 'unknown_scheduled_event_suffix';

  const isFirstDedupMarketing: boolean = persistCalendlyReturnDemoDedupOnceMarketingKey(
    suffixSegment,
    'embed',
  );
  if (!isFirstDedupMarketing) {
    return;
  }

  pushWorkyMarketingAnalyticsEvent({
    event: TAG_DICTIONARY.EVENTS.DEMO_BOOKED,
    calendly_booking_source: TAG_DICTIONARY.CALENDLY_BOOKING_SOURCES.EMBED,
    calendly_scheduled_event_uri_suffix: suffixSegment,
  });
};

export {
  mergeTrafficAndMarketingBodyMarketing,
  pushWorkyMarketingAnalyticsEvent,
  pushWorkyMarketingDemoBookedFromCalendlyEmbedScheduledOnce,
  pushWorkyMarketingDemoBookedFromCalendlyReturnHrefOnce,
};
